import { useMemo, useState } from "react";

import { Button, Row } from "react-bootstrap";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { listAgency } from "../../../_services/serviceService.ts";
import { FormType, Permissions } from "../../../common/enums/sharedEnums";
import { IBuilding } from "../reducers";
import {
  addBuilding,
  removeBuildingFile,
  updateBuilding,
  uploadBuildingFiles,
} from "../services";
import BuildingFeatureForm from "./BuildingFeatureForm.tsx";
import BuildingInfoForm from "./BuildingInfoForm.tsx";
import SelectAgency from "../../../common/components/SelectAgency.tsx";
import UploadDocumentForm from "../../../common/components/UploadDocumentForm.tsx";
import { Agency } from "../../../common/interfaces.ts";
import history from "../../../routes/history.ts";
import { IReferentials } from "../../referential/reducers";
import { BUILDING_DETAILS } from "../routes";

interface FormTypeText {
  title: string;
  success: string;
  error: string;
}

export const typeText: Record<FormType, FormTypeText> = {
  [FormType.CREATE]: {
    title: "J'ajoute un nouvel immeuble",
    success: "Nouvel immeuble enregistré",
    error:
      "Le nouvel immeuble n'est pas enregistré, veuillez corriger les erreurs du formulaire",
  },
  [FormType.UPDATE]: {
    title: "Modifier un immeuble",
    success: "La modification de l'immeuble a été enregistré",
    error:
      "La modification de l'immeuble n'a pas été enregistrée, veuillez corriger les erreurs du formulaire",
  },
};

const BuildingForm = ({
  building,
  formType,
  id,
  referentials,
}: {
  building?: IBuilding;
  formType: FormType.CREATE | FormType.UPDATE;
  id?: string;
  referentials: IReferentials;
}) => {
  const queryClient = useQueryClient();
  const [buildingDocuments, setBuildingDocuments] = useState<Array<any>>(
    building?.files ?? []
  );

  const { control, handleSubmit } = useForm<IBuilding>({
    defaultValues: useMemo(() => building, [building]),
  });

  const mutationBuildingCreate = useMutation({
    mutationFn: (buildingData: IBuilding) => addBuilding(buildingData),
    onError: (error) => {
      console.error(error);
      toast.error(typeText[formType].error);
    },
    onSuccess: async () => {
      toast.success("Building enregistré avec succès");
    },
  });

  const mutationBuildingUpdate = useMutation({
    mutationFn: (buildingData: IBuilding) =>
      updateBuilding(buildingData, id as string),
    onError: (error) => {
      console.error(error);
      toast.error(typeText[formType].error);
    },
    onSuccess: async () => {
      toast.success("Building enregistré avec succès");
    },
  });

  const mutationBuildingAddDocuments = useMutation({
    mutationFn: ({
      idBuilding,
      buildingDocuments,
    }: {
      idBuilding: string;
      buildingDocuments: any[];
    }) => uploadBuildingFiles(idBuilding, buildingDocuments),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement des fichiers");
    },
    onSuccess() {
      setBuildingDocuments([]);
    },
  });

  const mutationBuildingRemoveDocument = useMutation({
    mutationFn: ({
      idBuilding,
      fileId,
    }: {
      idBuilding: string;
      fileId: string;
    }) => removeBuildingFile(idBuilding, fileId),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la suppression du fichier");
    },
    onSuccess() {
      setBuildingDocuments([]);
    },
  });

  const onSubmit: SubmitHandler<IBuilding> = async (data) => {
    if (formType === FormType.UPDATE && id) {
      await mutationBuildingUpdate.mutateAsync(data);
    } else {
      await mutationBuildingCreate.mutateAsync(data);
    }

    if (buildingDocuments.length > 0 && id)
      await mutationBuildingAddDocuments.mutateAsync({
        idBuilding: id,
        buildingDocuments,
      });
    await queryClient.invalidateQueries({ queryKey: ["building", id] });
    toast.success(typeText[formType].success);
    id &&
      history.push(BUILDING_DETAILS.replace(":id", data.datedReference || id));
  };

  const {
    data: agencies,
    isLoading: loadingAgencies,
    error: errorAgencies,
  } = useQuery<Agency[], Error>(
    ["listAgency", Permissions.BUILDING_DWELLING_CREATE],
    () => listAgency(Permissions.BUILDING_DWELLING_CREATE)
  );

  return (
    <div className="bg-container">
      <div className="d-flex justify-content-between align-items-center">
        <h1 className="mb-3 text-center text-lg-start d-lg-block">
          {typeText[formType].title}
        </h1>

        <div className="bg-container">
          <div className="d-flex align-items-center gap-3">
            <label className="add-prop-label ms-2">Agence :</label>
            <div className="select-creatable-team-member-form">
              <Controller
                control={control}
                name={"agencyId"}
                render={({ field }) => {
                  return (
                    <>
                      <SelectAgency
                        agencies={agencies}
                        loading={loadingAgencies}
                        error={errorAgencies}
                        defaultValue={building?.agency.id}
                        rightValue={Permissions.BUILDING_DWELLING_CREATE}
                        setAgency={(value) => {
                          field.onChange(value.value);
                        }}
                      />
                    </>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <form className="mt-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="fw-bold" style={{ fontSize: "24px" }}>
          Caractéristiques de l'immeuble
        </h2>

        <div className="bg-container shadow mt-4">
          <BuildingFeatureForm control={control} referentials={referentials} />

          <Row className="mt-4">
            <UploadDocumentForm
              documents={buildingDocuments}
              setDocuments={setBuildingDocuments}
              referentials={referentials}
              removeFile={(key) =>
                id &&
                mutationBuildingRemoveDocument.mutate({
                  idBuilding: id,
                  fileId: key as string,
                })
              }
            />
          </Row>
        </div>

        <h2 className="fw-bold mt-5" style={{ fontSize: "24px" }}>
          Informations sur l'immeuble
        </h2>

        <div className="shadow p-3 mt-4 bg-container">
          <BuildingInfoForm control={control} />
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button variant="success" type={"submit"}>
            Enregistrer
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BuildingForm;
